import React, { createContext, useState, useContext, useEffect } from "react";

const ThemeContext = createContext();

export const themes = {
    slate: {
        name: "Slate",
        loginBackground: "linear-gradient(135deg, #0f172a 0%, #334155 100%)",
        toolbarAddButton: "bg-gradient-to-r from-slate-800 to-slate-900 text-white hover:from-slate-900 hover:to-black",
        toolbarExportButton: "bg-gradient-to-r from-slate-600 to-slate-700 text-white hover:from-slate-700 hover:to-slate-800",
        tableHeaderBg: "#374151",
        formHeaderBg: "bg-gradient-to-r from-slate-700 to-slate-800",
        formSubText: "text-slate-300",
        // Filter panel colors
        filterIconBg: "#334155",
        filterAccent: "#475569",
        filterAccentHover: "#334155",
        filterCheckboxChecked: "#475569",
        filterSelectedBg: "#e2e8f0",
        filterSelectedText: "#1e293b",
        // Client panel colors
        clientAvatarGradient: "linear-gradient(135deg, #475569 0%, #1e293b 100%)",
        clientContactAvatarGradient: "linear-gradient(135deg, #475569 0%, #334155 100%)",
        clientAccentColor: "#475569",
        // Primary button colors
        primaryButtonBg: "#475569",
        primaryButtonHover: "#334155",
    },
    blue: {
        name: "Classic Blue",
        loginBackground: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        toolbarAddButton: "bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700",
        toolbarExportButton: "bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:from-purple-600 hover:to-purple-700",
        tableHeaderBg: "#2563eb",
        formHeaderBg: "bg-gradient-to-r from-blue-600 to-indigo-600",
        formSubText: "text-blue-100",
        // Filter panel colors
        filterIconBg: "#4f46e5",
        filterAccent: "#6366f1",
        filterAccentHover: "#4f46e5",
        filterCheckboxChecked: "#6366f1",
        filterSelectedBg: "#e0e7ff",
        filterSelectedText: "#3730a3",
        // Client panel colors
        clientAvatarGradient: "linear-gradient(135deg, #667eea 0%, #4f46e5 100%)",
        clientContactAvatarGradient: "linear-gradient(135deg, #818cf8 0%, #6366f1 100%)",
        clientAccentColor: "#6366f1",
        // Primary button colors
        primaryButtonBg: "#4f46e5",
        primaryButtonHover: "#4338ca",
    },
    purple: {
        name: "Royal Purple",
        loginBackground: "linear-gradient(135deg, #9333ea 0%, #4f46e5 100%)",
        toolbarAddButton: "bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700",
        toolbarExportButton: "bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white hover:from-fuchsia-600 hover:to-purple-700",
        tableHeaderBg: "#7c3aed",
        formHeaderBg: "bg-gradient-to-r from-purple-600 to-indigo-600",
        formSubText: "text-purple-100",
        // Filter panel colors
        filterIconBg: "#7c3aed",
        filterAccent: "#8b5cf6",
        filterAccentHover: "#7c3aed",
        filterCheckboxChecked: "#8b5cf6",
        filterSelectedBg: "#ede9fe",
        filterSelectedText: "#5b21b6",
        // Client panel colors
        clientAvatarGradient: "linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)",
        clientContactAvatarGradient: "linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)",
        clientAccentColor: "#8b5cf6",
        // Primary button colors
        primaryButtonBg: "#7c3aed",
        primaryButtonHover: "#6d28d9",
    },
    teal: {
        name: "Ocean Teal (Default)",
        loginBackground: "linear-gradient(135deg, #0d9488 0%, #0f766e 100%)",
        toolbarAddButton: "bg-gradient-to-r from-teal-600 to-teal-700 text-white hover:from-teal-700 hover:to-teal-800",
        toolbarExportButton: "bg-gradient-to-r from-teal-500 to-teal-600 text-white hover:from-teal-600 hover:to-teal-700",
        tableHeaderBg: "#0f766e",
        formHeaderBg: "bg-gradient-to-r from-teal-600 to-teal-700",
        formSubText: "text-teal-100",
        // Filter panel colors
        filterIconBg: "#0d9488",
        filterAccent: "#14b8a6",
        filterAccentHover: "#0d9488",
        filterCheckboxChecked: "#14b8a6",
        filterSelectedBg: "#ccfbf1",
        filterSelectedText: "#115e59",
        // Client panel colors
        clientAvatarGradient: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)",
        clientContactAvatarGradient: "linear-gradient(135deg, #2dd4bf 0%, #14b8a6 100%)",
        clientAccentColor: "#14b8a6",
        // Primary button colors
        primaryButtonBg: "#0d9488",
        primaryButtonHover: "#0f766e",
    },
};

export const ThemeProvider = ({ children }) => {
    const [currentTheme, setCurrentTheme] = useState("teal");

    // Load theme from local storage on mount
    useEffect(() => {
        const savedTheme = localStorage.getItem("appTheme");
        if (savedTheme && themes[savedTheme]) {
            setCurrentTheme(savedTheme);
        }
    }, []);

    const changeTheme = (themeKey) => {
        if (themes[themeKey]) {
            setCurrentTheme(themeKey);
            localStorage.setItem("appTheme", themeKey);
        }
    };

    const theme = themes[currentTheme];

    return (
        <ThemeContext.Provider value={{ theme, changeTheme, currentTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => useContext(ThemeContext);
